import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AppLayout } from "./DashboardPage";
import { ReviewQueueContent } from "./ReviewQueuePage";
import { ReconcileContent } from "./ReconcilePage";
import { BillsContent } from "./BillsPage";
import { WeeklyCloseContent } from "./WeeklyClosePage";
import { ContextHeader } from "../components/ContextHeader";
import { CycleHeader } from "../components/rhythm/CycleHeader";
import { CyclePositionIndicator } from "../components/rhythm/CyclePositionIndicator";
import { ListChecks, FileCheck, CalendarDays, RefreshCw } from "lucide-react";

const TABS = [
  { key: "review", label: "Review", icon: ListChecks },
  { key: "reconcile", label: "Confirm", icon: FileCheck },
  { key: "bills", label: "Bills", icon: CalendarDays },
  { key: "close-cycle", label: "Reflect & Reset", icon: RefreshCw },
];

export default function TheClosePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "review");

  // Sync tab state with URL search params
  useEffect(() => {
    const tabParam = searchParams.get("tab") || "review";
    if (tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const switchTab = (key) => {
    setActiveTab(key);
    if (key === "review") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: key });
    }
  };

  const TAB_CONTEXT = {
    review: {
      title: "Review what needs attention",
      purpose: "Let's quickly look over anything that needs a decision.",
      nextStep: "Confirm your activity",
      onNextClick: () => switchTab("reconcile"),
    },
    reconcile: {
      title: "Confirm your activity",
      purpose: "Confirm your transactions so everything is clear and accurate.",
      nextStep: "See your bills",
      onNextClick: () => switchTab("bills"),
    },
    bills: {
      title: "What needs to be paid",
      purpose: "Stay aware of what's coming up.",
      nextStep: "Look back on this cycle",
      onNextClick: () => switchTab("close-cycle"),
    },
    "close-cycle": {
      title: "Look back on this cycle",
      purpose: "A simple view of what came in, what went out, and where you stand.",
      nextStep: "Start your next cycle clear",
      nextHref: "/plan",
    },
  };

  const ctx = TAB_CONTEXT[activeTab] || TAB_CONTEXT.review;

  return (
    <AppLayout>
      <div data-testid="the-close-page">
        {/* Tab navigation */}
        <div className="border-b border-border bg-card/50 sticky top-0 z-10 backdrop-blur-sm">
          <div className="flex items-center gap-2 px-6 py-3">
            <h1 className="font-heading text-lg font-semibold text-foreground mr-4">Review & reset</h1>
            <div className="flex items-center gap-1.5 bg-muted/50 rounded-lg p-1" data-testid="close-tabs">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => switchTab(tab.key)}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-md transition-all duration-150 cursor-pointer ${
                      isActive
                        ? "bg-amber-600 text-white shadow-sm"
                        : "text-muted-foreground hover:bg-amber-50 dark:hover:bg-amber-950/30 hover:text-amber-700 dark:hover:text-amber-300"
                    }`}
                    data-testid={`close-tab-${tab.key}`}
                  >
                    <tab.icon className="w-3.5 h-3.5" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Context Header */}
        <ContextHeader section="REVIEW & RESET" color="amber" {...ctx} />

        {/* Cycle Header + Position */}
        <div className="px-6 pt-4">
          <CycleHeader />
          <CyclePositionIndicator />
        </div>

        {/* Tab content */}
        {activeTab === "review" && <ReviewQueueContent />}
        {activeTab === "reconcile" && <ReconcileContent />}
        {activeTab === "bills" && <BillsContent />}
        {activeTab === "close-cycle" && <WeeklyCloseContent />}
      </div>
    </AppLayout>
  );
}
